import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader, KpiShell, KPI_ROW, PanelLoading } from '../components/ui.jsx'
import { BarChart } from '../components/charts.jsx'
import DataTable, { StatusPill } from '../components/DataTable.jsx'
import Select from '../components/Select.jsx'
import {
  HeartPulseIcon,
  CloudIcon,
  EventsIcon,
  ClockIcon,
  TagIcon,
  TextIcon,
  HashIcon,
  RefreshIcon,
  SettingsIcon,
  CalendarIcon,
  BuildingIcon,
} from '../components/icons.jsx'
import { formatPct, relativeAgo } from '../lib/sampleData.js'
import {
  fetchSoftwareSnapshot,
  shapeSnapshot,
  SOFTWARE_WINDOW_KEYS,
  SOFTWARE_WINDOW_LABELS,
  DEFAULT_SOFTWARE_WINDOW,
  DEPLOYMENT_LABELS,
  isSoftwareWindow,
} from '../lib/softwareHealth.js'

// SOFTWARE — production health of the Revailo app, read live from the
// workloads-production account on every visit (lib/softwareHealth.js has the
// whole path). Every number on this page is production's own: the green health
// check's test runs, api.revailo.com's traffic, the blue/green lambdas, the
// scheduled jobs against their schedules, and the deployment settings the
// router reads. A part production could not read is said to be missing, never
// filled in.
//
// Two kinds of number, grouped so the difference shows: the health check, the
// scheduled jobs and the environment are AS OF NOW and ignore the window;
// traffic, deployments and the error log count the window, and the deltas
// compare it with the equal window before.

const SECTION_H = 'm-0 mb-2 text-[16px] font-semibold text-ink'
// Chart cards cap themselves at 900px, the page does not — see Activity's
// CARD constant for why a stretched bar chart stops reading as a shape.
const CARD = 'mb-5 max-w-[900px] rounded-[12px] border border-line bg-surface p-5 shadow-card'
const PANEL = 'rounded-[12px] border border-line bg-surface p-5 shadow-card'
const BOX = 'rounded-[12px] border border-line bg-surface p-10 text-center text-dim'
// A loading line is not a card (the Activity page's rule): the words alone,
// centred in the space the page is about to take.
const WARN_BOX =
  'mb-5 rounded-[12px] border border-[#fde68a] bg-[#fefce8] p-4 text-[13px] text-[#a16207] dark:border-[rgba(161,98,7,0.4)] dark:bg-[rgba(161,98,7,0.14)] dark:text-[#fbbf24]'
const GHOST_BTN =
  'inline-flex cursor-pointer items-center gap-x-[7px] rounded-[9px] border border-line bg-surface px-3 py-2 text-[13px] font-semibold text-ink transition-colors hover:bg-panel disabled:cursor-not-allowed disabled:opacity-45'
// The danger hue is a fixed hex in tailwind.config.js (not a CSS variable),
// so the chart gets the literal — `var(--danger)` would resolve to nothing.
const DANGER_HEX = '#dc2626'

const WINDOW_OPTIONS = SOFTWARE_WINDOW_KEYS.map((key) => ({ value: key, label: SOFTWARE_WINDOW_LABELS[key] }))

const SERVICE_TONE = { operational: 'success', degraded: 'warning', down: 'danger', unknown: 'neutral' }
const SERVICE_LABEL = { operational: 'Passing', degraded: 'Tests failing', down: 'Not running', unknown: 'No result' }
const OVERALL_LABEL = { operational: 'Operational', degraded: 'Degraded', down: 'Down', unknown: 'Unknown' }
const STATUS_CLASS = { operational: 'text-good', degraded: 'text-warn', down: 'text-danger', unknown: 'text-dim' }
const STRIP_BG = { ok: 'bg-good', degraded: 'bg-warn', down: 'bg-danger', unknown: 'bg-faint', none: 'bg-panel' }
const JOB_TONE = { on_time: 'success', not_due: 'neutral', failed: 'warning', missed: 'danger', no_target: 'warning', disabled: 'neutral', unknown: 'neutral' }
const JOB_LABEL = { on_time: 'On time', not_due: 'Not due yet', failed: 'Errored', missed: 'Missed', no_target: 'No target', disabled: 'Disabled', unknown: 'Unknown' }
const LEVEL_TONE = { error: 'danger', warn: 'warning' }
const LEVEL_LABEL = { error: 'Error', warn: 'Warning' }
const DEPLOYMENT_TONE = { blue: 'info', green: 'success', router: 'neutral', health_check: 'neutral', shared: 'neutral' }

const num = (v) => (v == null ? '—' : Number(v).toLocaleString())
const plural = (n, one, many = `${one}s`) => `${num(n)} ${n === 1 ? one : many}`
const msText = (v) => (v == null ? '—' : `${num(Math.round(v))} ms`)
const secondsText = (v) => (v == null ? '—' : v >= 60 ? `${(v / 60).toFixed(1)} min` : `${v.toFixed(1)} s`)

function When({ iso, dateOnly = false, weekday = false, title }) {
  if (!iso) return <span className="text-faint">—</span>
  const d = new Date(iso)
  // `weekday`: within the coming week a weekday says it best ("Mon 7:00 AM" —
  // the health check skips weekends); further out, the date.
  const soon = weekday && d.getTime() - Date.now() < 6 * 86400 * 1000
  const text = dateOnly
    ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : soon
      ? d.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })
      : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  return (
    <span title={title || d.toLocaleString()} className="whitespace-nowrap">
      {text}
    </span>
  )
}

// "12m ago" with the absolute instant behind it, for a hover.
function Ago({ iso, now }) {
  if (!iso) return <span className="text-faint">—</span>
  return (
    <span title={new Date(iso).toLocaleString()} className="whitespace-nowrap">
      {relativeAgo(iso, now)}
    </span>
  )
}

// One cell per recent health-check run, oldest on the left, followed by the
// share that passed. Colour is never the only carrier: every cell names its
// run and verdict on hover, and the percentage sits beside the strip in text.
function RunStrip({ strip, passRate }) {
  return (
    <div className="flex items-center gap-x-2">
      <div className="flex items-center gap-x-px" aria-label="Recent health-check runs">
        {strip.map((c) => (
          <span
            key={c.key}
            title={`${c.at ? new Date(c.at).toLocaleString() : ''} — ${c.word}`}
            className={`inline-block h-[10px] w-[4px] rounded-[1px] ${STRIP_BG[c.state] || 'bg-panel'}`}
          />
        ))}
      </div>
      <span className="text-[12.5px] tabular-nums text-dim">{formatPct(passRate, 0)}</span>
    </div>
  )
}

function DeploymentTag({ deployment }) {
  if (!deployment) return <span className="text-faint">—</span>
  return <StatusPill tone={DEPLOYMENT_TONE[deployment] || 'neutral'}>{DEPLOYMENT_LABELS[deployment] || deployment}</StatusPill>
}

const serviceColumns = () => [
  {
    key: 'name',
    header: 'API',
    icon: CloudIcon,
    maxWidth: 230,
    render: (s) => (
      <div className="min-w-0">
        <div className="truncate font-semibold text-ink">{s.name}</div>
        <div className="mt-[2px] truncate text-[12px] text-faint">
          {s.tests
            ? `${num(s.tests.passed)} passed · ${num(s.tests.failed)} failed · ${num(s.tests.skipped)} skipped`
            : 'no result in the recent runs'}
        </div>
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Latest run',
    render: (s) => (
      <div className="min-w-0">
        <StatusPill tone={SERVICE_TONE[s.status] || 'neutral'}>
          {SERVICE_LABEL[s.status] || s.status}
        </StatusPill>
        {s.status !== 'operational' && (s.reason || s.failures[0]) && (
          <div className="mt-1 max-w-[220px] truncate text-[12px] text-dim" title={s.reason || `${s.failures[0].name} — ${s.failures[0].detail || ''}`}>
            {s.reason || s.failures[0].name}
          </div>
        )}
      </div>
    ),
  },
  { key: 'durationSeconds', header: 'Test time', icon: ClockIcon, numeric: true, render: (s) => <span className="tabular-nums">{secondsText(s.durationSeconds)}</span> },
  { key: 'strip', header: 'Recent runs', icon: HeartPulseIcon, render: (s) => <RunStrip strip={s.strip} passRate={s.passRate} /> },
  {
    key: 'availability30d',
    header: 'Ran · 30 days',
    icon: HeartPulseIcon,
    numeric: true,
    render: (s) => (
      <span className="tabular-nums" title={`${num(s.availabilityRuns)} check runs in 30 days`}>
        {formatPct(s.availability30d, 1)}
      </span>
    ),
  },
]

const deploymentColumns = () => [
  {
    key: 'name',
    header: 'Deployment',
    icon: BuildingIcon,
    maxWidth: 220,
    render: (d) => (
      <div className="min-w-0">
        <div className="truncate font-semibold text-ink">{d.name}</div>
        <div className="mt-[2px] truncate text-[12px] text-faint" title={d.role}>{d.role}</div>
      </div>
    ),
  },
  { key: 'functions', header: 'Functions', icon: HashIcon, numeric: true, render: (d) => <span className="tabular-nums">{num(d.functions)}</span> },
  {
    key: 'invocations',
    header: 'Invocations',
    icon: EventsIcon,
    numeric: true,
    render: (d) => (
      <span className="whitespace-nowrap tabular-nums">
        {num(d.invocations)}
        {d.invocationsDelta?.text && <span className="ml-1.5 text-[12px] text-dim">{d.invocationsDelta.text}</span>}
      </span>
    ),
  },
  {
    key: 'errors',
    header: 'Errors',
    icon: TagIcon,
    numeric: true,
    render: (d) => (
      <span className={`whitespace-nowrap tabular-nums ${d.errors ? 'text-danger' : ''}`}>
        {num(d.errors)}
        {d.errorRate != null && d.errors > 0 && <span className="ml-1.5 text-[12px] text-dim">{formatPct(d.errorRate, 2)}</span>}
      </span>
    ),
  },
  { key: 'throttles', header: 'Throttles', icon: HashIcon, numeric: true, render: (d) => <span className="tabular-nums">{num(d.throttles)}</span> },
  {
    key: 'servers',
    header: 'Servers',
    icon: CloudIcon,
    render: (d) =>
      d.servers.length ? (
        <span className="text-dim" title={d.servers.map((g) => g.label).join(' · ')}>
          {d.servers.map((g) => (g.desired === 0 && g.max === 0 ? 'scaled to zero' : `${g.in_service} of ${g.desired} running`)).join(' · ')}
        </span>
      ) : (
        <span className="text-faint">—</span>
      ),
  },
  { key: 'lastCodeChange', header: 'Last change', icon: CalendarIcon, render: (d) => <When iso={d.lastCodeChange} dateOnly /> },
]

const jobColumns = (now) => [
  {
    key: 'name',
    header: 'Job',
    icon: RefreshIcon,
    maxWidth: 185,
    render: (j) => (
      <div className="min-w-0">
        <div className="truncate font-semibold text-ink" title={j.name}>{j.name}</div>
        <div className="mt-[2px] flex min-w-0 items-center gap-x-1.5 text-[12px] text-faint">
          {j.deployment && j.deployment !== 'shared' && <DeploymentTag deployment={j.deployment} />}
          <span className="truncate" title={j.target || ''}>{j.target || 'no target'}</span>
        </div>
      </div>
    ),
  },
  { key: 'schedule', header: 'Schedule', icon: CalendarIcon, wrap: true, maxWidth: 180, render: (j) => <span className="inline-block min-w-[130px] text-dim" title={j.expression}>{j.schedule}</span> },
  {
    key: 'status',
    header: 'Status',
    render: (j) => (
      <StatusPill tone={JOB_TONE[j.status] || 'neutral'}>
        {JOB_LABEL[j.status] || j.status}
      </StatusPill>
    ),
  },
  {
    key: 'lastRunAt',
    header: 'Last run',
    icon: HeartPulseIcon,
    render: (j) => (j.lastRunAt ? <Ago iso={j.lastRunAt} now={now} /> : <span className="text-faint">—</span>),
  },
  { key: 'nextAt', header: 'Next run', icon: ClockIcon, render: (j) => <When iso={j.nextAt} weekday /> },
  {
    key: 'invocations',
    header: 'Runs',
    icon: EventsIcon,
    numeric: true,
    render: (j) => (
      <div className="tabular-nums">
        <div>{num(j.invocations)}</div>
        {j.errors > 0 && <div className="mt-[2px] text-[12px] text-danger">{plural(j.errors, 'error')}</div>}
      </div>
    ),
  },
]

const errorColumns = () => [
  {
    key: 'level',
    header: 'Level',
    icon: TagIcon,
    sortable: true,
    render: (e) => (
      <StatusPill tone={LEVEL_TONE[e.level] || 'neutral'}>
        {LEVEL_LABEL[e.level] || e.level}
      </StatusPill>
    ),
  },
  {
    key: 'source',
    header: 'Source',
    icon: CloudIcon,
    sortable: true,
    maxWidth: 220,
    render: (e) => (
      <div className="min-w-0">
        <div className="truncate text-[12.5px] font-semibold text-ink" title={e.source}>{e.source}</div>
        <div className="mt-[2px] truncate text-[12px] text-faint">{e.kind}</div>
      </div>
    ),
  },
  { key: 'message', header: 'Message', icon: TextIcon, wrap: true, maxWidth: 360, render: (e) => <span className="text-[12.5px] text-ink">{e.message}</span> },
  { key: 'count', header: 'Count', icon: HashIcon, numeric: true, sortable: true, render: (e) => <span className="tabular-nums">{num(e.count)}</span> },
  {
    key: 'lastSeenAt',
    header: 'Last seen',
    icon: ClockIcon,
    sortable: true,
    render: (e) => (
      <When
        iso={e.lastSeenAt}
        title={e.firstSeenAt ? `${new Date(e.lastSeenAt).toLocaleString()} — first seen ${new Date(e.firstSeenAt).toLocaleString()}` : undefined}
      />
    ),
  },
]

// Words for a failed read, by what the caller can do about it.
function failureText(error) {
  if (!error) return ''
  if (error.status === 401) return 'Your session has expired. Sign in again to read production health.'
  return error.message || 'Production health could not be read.'
}

export default function Software() {
  const [searchParams, setSearchParams] = useSearchParams()
  const windowParam = searchParams.get('window')
  // An unknown ?window= is the default, never a guess.
  const windowKey = isSoftwareWindow(windowParam) ? windowParam : DEFAULT_SOFTWARE_WINDOW
  // { status: 'loading' | 'ok' | 'error', raw, error }. The last good snapshot
  // stays on screen (dimmed) while a new window or a refresh is read, so the
  // page does not blank out for the few seconds production takes.
  const [state, setState] = useState({ status: 'loading', raw: null, error: null })
  const [errorTab, setErrorTab] = useState('all')
  const requestId = useRef(0)

  const load = useCallback(() => {
    const id = ++requestId.current
    setState((prev) => ({ ...prev, status: 'loading', error: null }))
    fetchSoftwareSnapshot({ window: windowKey })
      .then((raw) => {
        if (id === requestId.current) setState({ status: 'ok', raw, error: null })
      })
      .catch((error) => {
        if (id === requestId.current) setState((prev) => ({ ...prev, status: 'error', error }))
      })
  }, [windowKey])

  useEffect(() => {
    load()
  }, [load])

  // Switching the window adjusts the page in place, so it REPLACES the
  // history entry; the default is written as no param at all.
  const setWindow = (next) => {
    setSearchParams(
      (prev) => {
        const out = new URLSearchParams(prev)
        if (next === DEFAULT_SOFTWARE_WINDOW) out.delete('window')
        else out.set('window', next)
        return out
      },
      { replace: true },
    )
  }

  const snap = useMemo(() => (state.raw ? shapeSnapshot(state.raw) : null), [state.raw])
  // "Now" for every relative time on the page is the snapshot's own instant,
  // so a row's "12m ago" is measured against the same clock as its read.
  const now = useMemo(() => (snap?.asOf ? new Date(snap.asOf) : new Date()), [snap])
  const errors = snap ? snap.errors : []
  const errorTabs = useMemo(() => {
    const errorsOnly = errors.filter((e) => e.level === 'error').length
    return [
      { key: 'all', label: 'All', count: errors.length },
      { key: 'error', label: 'Errors', count: errorsOnly, predicate: (e) => e.level === 'error' },
      { key: 'warn', label: 'Warnings', count: errors.length - errorsOnly, predicate: (e) => e.level === 'warn' },
    ]
  }, [errors])
  const svcColumns = useMemo(() => serviceColumns(), [])
  const depColumns = useMemo(() => deploymentColumns(), [])
  const jobsColumns = useMemo(() => jobColumns(now), [now])
  const errColumns = useMemo(() => errorColumns(), [])

  const loading = state.status === 'loading'
  const header = (
    <PageHeader
      title="Software"
      actions={
        <>
          {snap && (
            <span title={`Read from production (workloads-production) at ${now.toLocaleString()}`} className="cursor-help">
              <StatusPill tone="success">
                Live · {now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
              </StatusPill>
            </span>
          )}
          <Select ariaLabel="Window" value={windowKey} onChange={setWindow} options={WINDOW_OPTIONS} buttonClassName="min-w-[160px]" />
          <button type="button" className={GHOST_BTN} onClick={load} disabled={loading} title="Read production again">
            <RefreshIcon className="h-[15px] w-[15px] text-dim" aria-hidden="true" />
            {loading ? 'Reading…' : 'Refresh'}
          </button>
        </>
      }
    />
  )

  if (!snap) {
    return (
      <div className="flex min-h-full flex-col">
        {header}
        {state.status === 'error' ? (
          <div className={BOX}>{failureText(state.error)}</div>
        ) : (
          <PanelLoading label="Reading production…" />
        )}
      </div>
    )
  }

  const s = snap.summary
  const windowLabel = SOFTWARE_WINDOW_LABELS[snap.window] || SOFTWARE_WINDOW_LABELS[windowKey]
  const inWindow = windowLabel.toLowerCase()
  const bucketWord = snap.series?.bucket === 'hour' ? 'hour' : 'day'
  const priorNote = `vs the ${snap.window === '24h' ? '24 hours' : snap.window === '7d' ? '7 days' : '30 days'} before`
  const statusNote = !snap.sections.healthCheck
    ? 'health check not read'
    : s.problemApis.length
      ? `${s.apisPassing} of ${s.apisTotal} APIs passing`
      : `all ${s.apisTotal} APIs passing`

  return (
    <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'} aria-busy={loading}>
      {header}

      {state.status === 'error' && (
        <div className={WARN_BOX} role="status">
          {failureText(state.error)} Showing the last read, from {now.toLocaleTimeString()}.
        </div>
      )}

      <h2 className={SECTION_H}>Health now</h2>
      <section className={`mb-6 ${KPI_ROW}`}>
        <KpiShell
          label="Status"
          icon={HeartPulseIcon}
          labelTitle="The worst result any API had in the latest health-check run against green: tests failing makes production degraded, a check that could not run makes it down. The check runs every 10 minutes on weekdays, 07:00–18:50 Chicago."
          valueClass={STATUS_CLASS[s.status] || 'text-ink'}
          note={s.runningSince ? `${statusNote} · a run is in progress` : `${statusNote} · checked ${s.lastRunAt ? relativeAgo(s.lastRunAt, now) : '—'}`}
        >
          {OVERALL_LABEL[s.status] || s.status}
        </KpiShell>
        <KpiShell
          label="Ran · 30 days"
          icon={HeartPulseIcon}
          labelTitle="Across every API, the share of health-check runs in the last 30 days that completed without an infrastructure failure (a crash, a timeout, a failed sign-in). Test failures inside a completed run are counted in Status, not here. The window does not change it."
          note="health-check runs that completed"
        >
          {formatPct(s.availability30d, 2)}
        </KpiShell>
        <KpiShell
          label="Requests"
          icon={EventsIcon}
          labelTitle={`Requests to api.revailo.com in the window — blue and green both pass through its router. Delta is ${priorNote}.`}
          note={inWindow}
          delta={s.requestsDelta}
        >
          {num(s.requests)}
        </KpiShell>
        <KpiShell
          label="Server error rate"
          icon={TagIcon}
          labelTitle={`Requests to api.revailo.com that ended in a 5xx, as a share of all requests in the window. A rising rate paints red. Delta is ${priorNote}.`}
          note={`${plural(s.errors5xx ?? 0, '5xx')} · ${num(s.errors4xx)} 4xx`}
          delta={s.errorRateDelta}
        >
          {formatPct(s.errorRate, 2)}
        </KpiShell>
        <KpiShell
          label="p95 latency"
          icon={ClockIcon}
          labelTitle={`The 95th-percentile response time of api.revailo.com across the whole window. Slower paints red. Delta is ${priorNote}.`}
          note={inWindow}
          delta={s.p95Delta}
        >
          {msText(s.p95LatencyMs)}
        </KpiShell>
        <KpiShell
          label="Scheduled jobs"
          icon={RefreshIcon}
          labelTitle="Enabled scheduled jobs whose most recent due run actually happened — each matched against its own schedule, so two rules sharing one lambda never take credit for each other. Disabled jobs are left out."
          valueClass={s.problemJobs.length ? 'text-warn' : 'text-ink'}
          note={s.problemJobs.length ? `${s.problemJobs.length} need attention` : 'every job on schedule'}
        >
          {num(s.jobsHealthy)} of {num(s.jobsTotal)}
        </KpiShell>
      </section>

      {snap.issues.length > 0 && (
        <div className={WARN_BOX} role="status">
          <div className="font-semibold">{plural(snap.issues.length, 'thing needs', 'things need')} attention</div>
          {snap.issues.map((i) => (
            <div key={i.key} className="mt-1">
              <span className="font-semibold">{i.title}</span> {i.detail}
              {i.at && (
                <span className="text-[12px] opacity-80">
                  {' '}
                  · {relativeAgo(i.at, now)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <h2 className={SECTION_H} title="The test suite (testing/test_<api>.py) run against the green deployment by green-health-check-state-machine, one API at a time. Each strip cell is one of the recent runs, oldest on the left.">
        Health check
      </h2>
      <div className="mb-6">
        {snap.sections.healthCheck ? (
          <DataTable
            tableId="software-health-check"
            columns={svcColumns}
            rows={snap.services}
            rowKey={(row) => row.key}
            resizableColumns={false}
            columnPicker={false}
            emptyMessage="The health check has no APIs."
          />
        ) : (
          <div className={BOX}>The health check could not be read.</div>
        )}
      </div>

      <h2 className={SECTION_H}>Traffic</h2>
      {snap.series ? (
        <>
          <section className={CARD}>
            <h3 className="m-0 mb-1 text-[14px] font-semibold text-ink" title={`Requests to api.revailo.com per ${bucketWord}. The last bar is the ${bucketWord} still filling.`}>
              Requests — per {bucketWord}
            </h3>
            <div className="mb-3 text-[12px] text-faint">
              {num(s.requests)} requests across {inWindow}; the last bar is the {bucketWord} still filling.
            </div>
            <BarChart data={snap.series.requests} keyOf={(d) => d.key} labelEvery="auto" height={170} formatValue={(v) => plural(v, 'request')} />
          </section>
          <section className={CARD}>
            <h3 className="m-0 mb-1 text-[14px] font-semibold text-ink" title={`Requests to api.revailo.com that ended in a 5xx, per ${bucketWord}.`}>
              Server errors — per {bucketWord}
            </h3>
            <div className="mb-3 text-[12px] text-faint">
              {plural(s.errors5xx ?? 0, 'server error')} across {inWindow}, {formatPct(s.errorRate, 2)} of requests.
            </div>
            <BarChart data={snap.series.errors} keyOf={(d) => d.key} labelEvery="auto" height={170} color={DANGER_HEX} formatValue={(v) => plural(v, 'error')} />
          </section>
          <section className={CARD}>
            <h3 className="m-0 mb-1 text-[14px] font-semibold text-ink" title={`The 95th-percentile response time of api.revailo.com per ${bucketWord}, in milliseconds. A ${bucketWord} with no requests has no bar.`}>
              p95 latency — per {bucketWord}
            </h3>
            <div className="mb-3 text-[12px] text-faint">
              {msText(s.p95LatencyMs)} at the 95th percentile across {inWindow}.
            </div>
            <BarChart data={snap.series.latency} keyOf={(d) => d.key} labelEvery="auto" height={170} formatValue={msText} />
          </section>
        </>
      ) : (
        <div className={`${BOX} mb-6`}>Traffic could not be read.</div>
      )}

      <h2 className={SECTION_H} title={`Every lambda in production, grouped by name prefix. Counts are ${inWindow}; the delta beside invocations is ${priorNote}.`}>
        Deployments
      </h2>
      <div className="mb-6">
        {snap.sections.deployments ? (
          <DataTable
            tableId="software-deployments"
            columns={depColumns}
            rows={snap.deployments}
            rowKey={(row) => row.key}
            resizableColumns={false}
            columnPicker={false}
            emptyMessage="No lambdas found."
          />
        ) : (
          <div className={BOX}>Deployments could not be read.</div>
        )}
      </div>

      <h2 className={SECTION_H} title="EventBridge Scheduler schedules and EventBridge cron rules. A job is on time when its most recent due run has a real invocation of its target beside it. Runs and Errors are the target lambda's own counts in the window — a lambda shared by two jobs shows the same counts on both rows.">
        Scheduled jobs
      </h2>
      <div className="mb-6">
        {snap.sections.jobs ? (
          <DataTable
            tableId="software-jobs"
            columns={jobsColumns}
            rows={snap.jobs}
            rowKey={(row) => row.key}
            resizableColumns={false}
            columnPicker={false}
            emptyMessage="No scheduled jobs."
          />
        ) : (
          <div className={BOX}>Scheduled jobs could not be read.</div>
        )}
      </div>

      <h2 className={SECTION_H} title={`Lambdas that errored ${inWindow}, and the failures in the last ${snap.runsRead} health-check runs (assertion failures are warnings; a check that could not run is an error).`}>
        Error log
      </h2>
      <div className="mb-6">
        <DataTable
          tableId="software-errors"
          columns={errColumns}
          rows={errors}
          rowKey={(row) => row.id}
          searchable
          searchPlaceholder="Search the error log…"
          searchValue={(e) => `${e.message} ${e.source} ${e.kind} ${LEVEL_LABEL[e.level] || e.level}`}
          tabs={errorTabs}
          activeTab={errorTab}
          onTabChange={setErrorTab}
          pageSize={20}
          emptyMessage={`Nothing errored in ${inWindow}.`}
        />
      </div>

      <h2 className={SECTION_H}>Environment</h2>
      <section className={`${PANEL} mb-6`}>
        <div className="flex items-center gap-x-2 text-[13px] font-semibold text-ink">
          <SettingsIcon className="h-[15px] w-[15px] text-faint" aria-hidden="true" />
          Production
        </div>
        {snap.environment.length ? (
          <dl className="m-0 mt-4 grid grid-cols-1 gap-x-8 gap-y-3 md:grid-cols-2">
            {snap.environment.map((row) => (
              <div key={row.key} className="flex flex-col gap-y-[2px] border-b border-line pb-3 last:border-b-0 md:[&:nth-last-child(2)]:border-b-0">
                <dt className="text-[11px] font-semibold uppercase tracking-[0.03em] text-faint">{row.label}</dt>
                <dd className="m-0 text-[13.5px] text-ink">
                  {row.value}
                  {row.note && <span className="ml-2 text-[12px] font-normal text-dim">{row.note}</span>}
                  {row.at && (
                    <span className="ml-2 text-[12px] font-normal text-faint" title={new Date(row.at).toLocaleString()}>
                      {row.atLabel || 'since'} {new Date(row.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <div className="mt-3 text-[13px] text-dim">The environment could not be read.</div>
        )}
      </section>
    </div>
  )
}
